import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Quote as QuoteIcon } from "lucide-react";
import {
  CASE_STUDIES,
  APPROVED_CASE_STUDIES,
} from "@/lib/case_studies";
import { fadeUp, EASE_OUT_QUART } from "@/lib/motion";

/**
 * CaseStudy
 * Named dealership case studies. Ships approved entries publicly; if
 * an entry is still awaiting client approval, LotWise can preview it
 * privately by visiting the site with `?preview=case-studies` in the
 * URL — otherwise, the section renders nothing.
 *
 * When approval lands: open /lib/case_studies.js, set `approved: true`,
 * fill in the real principal name, logo url, and hero photo. No code
 * changes required.
 */
export default function CaseStudy() {
  const showPreview =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("preview") === "case-studies";

  const items = showPreview ? CASE_STUDIES : APPROVED_CASE_STUDIES;
  if (items.length === 0) return null;

  return (
    <section
      data-testid="case-study-section"
      className="relative bg-white text-black"
    >
      <div className="container-lw section-lw">
        <div className="mb-14 grid gap-6 md:grid-cols-[1fr_auto] md:items-end md:mb-16">
          <div>
            <div className="eyebrow text-[#4F0DFF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#4F0DFF]" />
              Case studies
            </div>
            <h2 className="mt-5 h-display text-4xl md:text-5xl lg:text-6xl">
              Real dealerships, <br />
              <span className="text-gradient-lw">on the LotWise standard.</span>
            </h2>
          </div>
          <p className="max-w-md text-[15px] leading-relaxed text-[#4A5359]">
            Named partner dealerships share how LotWise Auto changed the
            operational shape of their online inventory. Additional
            case studies available on qualified calls.
          </p>
        </div>

        <div className="space-y-16">
          {items.map((c, i) => (
            <motion.article
              key={c.id}
              data-testid={`case-study-${c.id}`}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-100px" }}
              custom={i}
              className={`grid gap-10 md:gap-16 lg:grid-cols-[1.2fr_1fr] ${
                i % 2 ? "lg:grid-flow-col-dense" : ""
              }`}
            >
              {/* Hero photo */}
              <motion.div
                initial={{ opacity: 0, scale: 0.97 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 1, ease: EASE_OUT_QUART }}
                className={i % 2 ? "lg:col-start-2" : ""}
              >
                <div className="relative aspect-[4/3] w-full overflow-hidden rounded-[4px] border border-black/10 bg-black">
                  {c.hero_photo ? (
                    <img
                      src={c.hero_photo}
                      alt={
                        c.approved
                          ? `${c.dealership} lot photograph`
                          : "Case study lot photograph (draft placeholder)"
                      }
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{ objectPosition: "center 60%" }}
                      loading="lazy"
                    />
                  ) : (
                    <div className="grid h-full w-full place-items-center text-white/40">
                      Lot photograph pending
                    </div>
                  )}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)",
                    }}
                  />
                  {/* Corner badge */}
                  <div className="absolute left-4 top-4 inline-flex items-center gap-2 rounded-full bg-black/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white backdrop-blur-sm">
                    <span className="h-px w-4 bg-[#0AB5FF]" />
                    {c.approved ? "Case study" : "Draft"}
                  </div>
                  {!c.approved && (
                    <div className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-full bg-amber-500/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-black backdrop-blur-sm">
                      Pending client approval
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/70">
                        Dealership
                      </div>
                      <div className="mt-1 font-display text-lg font-semibold tracking-tight md:text-xl">
                        {c.dealership}
                      </div>
                    </div>
                    <div className="rounded-full bg-black/60 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/80 backdrop-blur-sm">
                      {c.service_model_label}
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Copy column */}
              <div className={i % 2 ? "lg:col-start-1 lg:row-start-1" : ""}>
                {/* Logo or text mark */}
                <div className="flex items-center gap-4">
                  {c.logo_url ? (
                    <img
                      src={c.logo_url}
                      alt={`${c.dealership} logo`}
                      className="h-8 w-auto"
                      loading="lazy"
                    />
                  ) : (
                    <span className="inline-flex items-center rounded-[4px] border border-black/15 bg-[#F4F5F7] px-3 py-1.5 font-display text-xs font-semibold uppercase tracking-[0.24em] text-black/60">
                      {c.approved
                        ? c.dealership
                        : "Dealership logo pending"}
                    </span>
                  )}
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/40">
                    {c.city ? c.city.charAt(0).toUpperCase() + c.city.slice(1) : ""}
                  </span>
                </div>

                {/* Quote */}
                <div className="mt-6">
                  <QuoteIcon className="h-8 w-8 text-[#4F0DFF]/40" />
                  <blockquote className="mt-3 font-display text-2xl font-medium leading-snug tracking-tight md:text-3xl">
                    &ldquo;{c.quote}&rdquo;
                  </blockquote>
                  <div className="mt-4 text-sm text-[#4A5359]">
                    <span className="font-semibold text-black">
                      {c.principal_name}
                    </span>
                    <span className="text-[#79858D]"> · {c.principal_role}</span>
                  </div>
                </div>

                {/* Metrics */}
                <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {c.metrics.map((m) => (
                    <div
                      key={m.label}
                      className="rounded-[6px] border border-black/10 bg-[#F4F5F7] p-4"
                    >
                      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/45">
                        {m.label}
                      </div>
                      <div className="mt-1.5 font-display text-sm font-semibold text-black">
                        {m.value}
                      </div>
                    </div>
                  ))}
                </div>

                {c.result_paragraph ? (
                  <p className="mt-8 text-[15px] leading-relaxed text-[#4A5359]">
                    {c.result_paragraph}
                  </p>
                ) : null}

                <Link
                  to="/request-a-visit"
                  data-testid={`case-study-cta-${c.id}`}
                  className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-black transition hover:text-[#4F0DFF]"
                >
                  Bring this standard to your dealership
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
